import type {RecordAsset} from '../types'

export type PdfIntent={companyId:string;sourceReportAssetId:string;sha256:string;version:string|null;filename:string;size:number}
export type PdfState='ready'|'existing'|'saved'|'unknown'|'retryable'|'failed'
export type PdfItem={intent:PdfIntent;assetId:string;state:PdfState;message:string;file?:File}
type Journal={version:1;manifest:string;attempts:{assetId:string;state:PdfState}[]}
type Storage=Pick<globalThis.Storage,'getItem'|'setItem'>
const uuid=/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const states:PdfState[]=['ready','existing','saved','unknown','retryable','failed']
function stop(message:string):never{throw Error(message)}

/** This manifest declares canonical identities, never names or storage URLs. */
export function parsePdfManifest(text:string):PdfIntent[]{
 if(new TextEncoder().encode(text).length>1048576)stop('Manifest exceeds 1 MiB.')
 let value:unknown;try{value=JSON.parse(text)}catch{stop('Choose valid PDF manifest JSON.')}
 if(!value||typeof value!=='object'||Array.isArray(value)||Object.keys(value).join()!=='items')stop('Expected only an items array.')
 const items=(value as {items:unknown}).items
 if(!Array.isArray(items)||!items.length||items.length>200)stop('Manifest must contain 1–200 PDFs.')
 const names=new Set(),sources=new Set()
 return items.map(raw=>{
  if(!raw||typeof raw!=='object'||Array.isArray(raw)||Object.keys(raw).sort().join()!=='companyId,filename,sha256,size,sourceReportAssetId,version')stop('Invalid PDF manifest fields.')
  const r=raw as PdfIntent
  // eslint-disable-next-line no-control-regex -- Versions follow the private-report contract.
  if(!uuid.test(r.companyId)||!uuid.test(r.sourceReportAssetId)||typeof r.sha256!=='string'||!/^[a-f0-9]{64}$/.test(r.sha256)||!(r.version===null||typeof r.version==='string'&&r.version.length>=1&&r.version.length<=80&&!/[\x00-\x1f\x7f]/.test(r.version))||typeof r.filename!=='string'||r.filename.length>240||!/^.+\.pdf$/i.test(r.filename)||/[\x00-\x1f\x7f/\\]/.test(r.filename)||!Number.isSafeInteger(r.size)||r.size<1||r.size>4194304)stop('Invalid PDF identity, digest, version, filename or size (maximum 4 MiB).')
  if(names.has(r.filename)||sources.has(r.sourceReportAssetId))stop('Duplicate filename or source mapping; resolve it before upload.')
  names.add(r.filename);sources.add(r.sourceReportAssetId)
  return {companyId:r.companyId,sourceReportAssetId:r.sourceReportAssetId,sha256:r.sha256,version:r.version,filename:r.filename,size:r.size}
 })
}
export const pdfJournalKey=(userId:string,workspaceId:string)=>`avertsignal:pdf-upload:${encodeURIComponent(JSON.stringify([userId,workspaceId]))}`
export function matchingPdf(asset:RecordAsset,intent:PdfIntent){
 return asset.recordType==='organization'&&asset.recordId===intent.companyId&&asset.assetType==='FILE'&&asset.contentType==='application/pdf'&&asset.sourceReportAssetId===intent.sourceReportAssetId&&asset.contentSha256===intent.sha256&&(asset.reportVersion||null)===intent.version&&asset.sizeBytes===intent.size
}
export function inspectPdfAssets(intent:PdfIntent,assets:RecordAsset[],attemptId?:string):RecordAsset|undefined{
 if(!Array.isArray(assets))stop('Canonical report access is unavailable.')
 if(assets.length>=500)stop('The workspace report read reached its 500-record limit. Canonical completeness cannot be confirmed; no upload is allowed.')
 const sources=assets.filter(a=>a.id===intent.sourceReportAssetId)
 if(sources.length!==1||sources[0].recordType!=='organization'||sources[0].recordId!==intent.companyId||sources[0].assetType!=='LINK')stop('Source link is missing or belongs to another company. Refresh canonical access.')
 if(sources[0].reportVersion!=null&&sources[0].reportVersion!==intent.version)stop('Manifest version conflicts with the canonical source link. Correct the manifest before uploading.')
 const attempt=attemptId?assets.filter(a=>a.id===attemptId):[]
 if(attempt.length>1||attempt.length===1&&!matchingPdf(attempt[0],intent))stop('Existing upload identity conflicts with this exact PDF intent.')
 const matches=assets.filter(a=>matchingPdf(a,intent))
 if(matches.length>1)stop('Ambiguous matching PDFs; review canonical attachments before proceeding.')
 return matches[0]
}
export async function verifyPdfFile(intent:PdfIntent,file:File){
 if(file.name!==intent.filename||file.size!==intent.size)stop('Selected PDF filename or size does not match its manifest.')
 const bytes=await file.arrayBuffer()
 const digest=Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256',bytes)),b=>b.toString(16).padStart(2,'0')).join('')
 if(digest!==intent.sha256)stop('Selected PDF SHA256 does not match its manifest.')
 return file
}

/** Local journal contains request intents only. Canonical CRM assets remain server-owned. */
export class PdfBatch {
 readonly items:PdfItem[]
 private busy=false
 private readonly manifest:string
 constructor(intents:PdfIntent[],private readonly deps:{key:string;storage:Storage;active:()=>boolean;read:()=>Promise<RecordAsset[]>;provenance:(intent:PdfIntent)=>Promise<{declaredSha256:string|null;declaredVersion:string|null}>;upload:(item:PdfItem)=>Promise<unknown>;changed?:()=>void;uuid?:()=>string}){
  this.manifest=JSON.stringify({items:intents})
  const raw=deps.storage.getItem(deps.key)
  let saved:Journal|undefined
  if(raw){
   try{saved=JSON.parse(raw)}catch{stop('Upload journal cannot be read. Keep the current files and resolve the saved attempt before starting again.')}
   if(!saved||saved.version!==1||typeof saved.manifest!=='string'||!Array.isArray(saved.attempts)||saved.attempts.some(a=>!a||!uuid.test(a.assetId)||!states.includes(a.state))||new Set(saved.attempts.map(a=>a.assetId)).size!==saved.attempts.length)stop('Upload journal is invalid; no new attempt was created.')
   if(saved.manifest!==this.manifest){
    if(saved.attempts.some(a=>!['ready','saved','existing'].includes(a.state)))stop('Another PDF batch has unresolved attempts. Reselect its original manifest and files first.')
    saved=undefined
   }else if(saved.attempts.length!==intents.length)stop('Upload journal mapping is invalid.')
  }
  this.items=intents.map((intent,index)=>({intent:Object.freeze({...intent}),assetId:saved?.attempts[index].assetId??(deps.uuid??(()=>crypto.randomUUID()))(),state:saved&&saved.attempts[index].state!=='ready'?'unknown':'ready',message:saved?'Retained attempt. Check canonical status before retrying.':'Not submitted.'}))
 }
 private check(){if(!this.deps.active())stop('Current Super Admin access is unavailable. Keep the same upload journal.')}
 private persist(){
  const value=JSON.stringify({version:1,manifest:this.manifest,attempts:this.items.map(({assetId,state})=>({assetId,state}))})
  this.deps.storage.setItem(this.deps.key,value)
  if(this.deps.storage.getItem(this.deps.key)!==value)stop('Upload request journal could not be saved. No new submission is allowed.')
 }
 private changed(){this.deps.changed?.()}
 private async declaration(intent:PdfIntent){
  const declared=await this.deps.provenance(intent);this.check()
  if(declared.declaredVersion!==intent.version||declared.declaredSha256!==null&&declared.declaredSha256!==intent.sha256)stop('Manifest hash/version conflicts with authenticated source provenance. Correct the manifest before uploading; no new file was submitted.')
 }
 async prepare(files:File[]){
  this.check()
  if(this.busy)stop('A PDF operation is already running.')
  if(files.length>200||new Set(files.map(f=>f.name)).size!==files.length||files.some(f=>!this.items.some(i=>i.intent.filename===f.name)))stop('Select only uniquely named PDFs from this manifest (maximum 200).')
  const assets=await this.deps.read();this.check()
  for(const item of this.items){
   const match=inspectPdfAssets(item.intent,assets,item.assetId)
   await this.declaration(item.intent)
   if(match){item.state=match.id===item.assetId?'saved':'existing';item.message='Canonical PDF verified; no upload needed (filename may differ).';continue}
   const file=files.find(f=>f.name===item.intent.filename)
   if(!file)stop(`Missing selected PDF: ${item.intent.filename}`)
   item.file=await verifyPdfFile(item.intent,file);this.check()
  }
  this.persist();this.changed()
 }
 async run(mode:'start'|'verify'|'retry'){
  if(this.busy)return
  this.check();this.busy=true
  try{
   if(mode!=='verify'&&this.items.some(item=>item.state==='unknown'||mode==='start'&&item.state==='retryable'))stop('Reconcile every unknown upload, then explicitly retry its retained ID before starting queued files.')
   // All canonical source mappings must be readable before any upload starts.
   const assets=await this.deps.read();this.check()
   for(const item of this.items){
    const match=inspectPdfAssets(item.intent,assets,item.assetId)
    await this.declaration(item.intent)
    if(match){item.state=match.id===item.assetId?'saved':'existing';item.message='Canonical PDF verified.'}
    else if(['saved','existing'].includes(item.state))stop('A previously confirmed PDF is no longer visible. Review access before another change.')
    else if(mode==='verify'&&item.state==='unknown'){item.state='retryable';item.message='No matching canonical asset is visible. An explicit retry keeps the same upload UUID and bytes.'}
   }
   this.persist();this.changed()
   if(mode==='verify')return
   const queue=this.items.filter(item=>mode==='start'?item.state==='ready':item.state==='retryable')
   let cursor=0,halted=false
   const worker=async()=>{
    while(cursor<queue.length&&this.deps.active()&&!halted){
     const item=queue[cursor++]
     if(!item.file){item.message='Reselect the original PDF bytes and review before retrying.';continue}
     // Persist unknown BEFORE crossing the network boundary, including crashes/reloads.
     item.state='unknown';item.message='Upload submitted; canonical confirmation pending.'
     try{this.check();this.persist()}catch(error){halted=true;item.message=error instanceof Error?error.message:'Journal unavailable.';this.changed();return}
     this.changed()
     try{
      this.check();await this.deps.upload(item);this.check()
      try{
       const current=await this.deps.read();this.check()
       const match=inspectPdfAssets(item.intent,current,item.assetId)
       if(!match)stop('Upload response received, but canonical attachment is not yet confirmed.')
       item.state=match.id===item.assetId?'saved':'existing';item.message='Canonical PDF verified.'
      }catch(error){halted=true;throw error}
     }catch(error){
      halted=true
      item.message=error instanceof Error?error.message:'Upload outcome unknown. Check canonical status before retrying.'
     }
     try{this.persist()}catch{halted=true;item.state='unknown';item.message='Local confirmation could not be saved. Check canonical status before retrying.'}
     this.changed()
    }
   }
   await Promise.all([worker(),worker()])
  }finally{this.busy=false;this.changed()}
 }
}
